import {getScrollableAncestor} from '@albert-io/atomic/helpers/AnchoredFixedPosition/AnchoredFixedPosition.react';
import {clipsOrRepaints, isContainingBlock} from 'lib/domLayout';

import {ANNOTATION_INDICATOR_PANE_ATTRIBUTE} from '../shared/annotationGutter';
import {ANNOTATION_REGION_ATTRIBUTE} from './annotationTextProjection';

/** Their content names them or labels a control, so an indicator inside would join the name. */
const NAMING_TAGS = new Set([
  'A',
  'BUTTON',
  'CAPTION',
  'FIGCAPTION',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'LABEL',
  'LEGEND',
  'SUMMARY',
  'TH'
]);

const NAMING_ROLES = new Set([
  'button',
  'checkbox',
  'columnheader',
  'heading',
  'link',
  'menuitem',
  'option',
  'radio',
  'rowheader',
  'switch',
  'tab',
  'treeitem'
]);

/** They may hold only particular children (a list only its items). */
const STRUCTURAL_TAGS = new Set([
  'COLGROUP',
  'DATALIST',
  'DL',
  'MENU',
  'OL',
  'OPTGROUP',
  'SELECT',
  'TABLE',
  'TBODY',
  'TFOOT',
  'THEAD',
  'TR',
  'UL'
]);

const STRUCTURAL_ROLES = new Set([
  'grid',
  'list',
  'listbox',
  'menu',
  'menubar',
  'radiogroup',
  'row',
  'rowgroup',
  'table',
  'tablist',
  'tree',
  'treegrid'
]);

/** Inside it, an indicator would be cut out of the margin or drawn wrong, join a name, or hide. */
const blocksIndicator = (element: Element): boolean =>
  element.hasAttribute(ANNOTATION_REGION_ATTRIBUTE) ||
  NAMING_TAGS.has(element.tagName) ||
  NAMING_ROLES.has(element.getAttribute('role') ?? '') ||
  element.hasAttribute('inert') ||
  element.getAttribute('aria-hidden') === 'true' ||
  clipsOrRepaints(element);

const admitsIndicator = (element: Element): boolean =>
  !STRUCTURAL_TAGS.has(element.tagName) &&
  !STRUCTURAL_ROLES.has(element.getAttribute('role') ?? '');

/** Marks the element `AnnotationIndicatorHost` portals indicators into. */
export const ANNOTATION_INDICATOR_HOST_ATTRIBUTE = 'data-annotation-indicator-host';

export const isIndicatorHost = (node: Node): boolean =>
  node instanceof Element && node.hasAttribute(ANNOTATION_INDICATOR_HOST_ATTRIBUTE);

/** Something inside the first other cell of `cell`'s row that can hold an indicator. */
const rowmateSibling = (cell: Element): Element | null => {
  const row = cell.parentElement;
  if (!row || (row.tagName !== 'TR' && row.getAttribute('role') !== 'row')) {
    return null;
  }
  return (
    Array.from(row.children)
      .filter((rowmate) => rowmate !== cell && !blocksIndicator(rowmate))
      .flatMap((rowmate) => Array.from(rowmate.children))
      .find((child) => !isIndicatorHost(child)) ?? null
  );
};

/**
 * The element a margin indicator for `element` goes beside in the DOM, so it is reached in
 * reading order: the deepest one on the way up to `scroller` whose parent can hold it —
 * never inside another region's text, a label, a header cell, or a box that would clip or
 * repaint it. A table cell it cannot enter hands it to the first cell in the same row that
 * can take it. Null when `element` is outside `scroller`.
 */
export const resolveIndicatorSibling = (element: Element, scroller: Element): Element | null => {
  const path: Element[] = [];
  for (let node: Element | null = element; node !== scroller; node = node.parentElement) {
    if (!node) {
      return null;
    }
    path.push(node);
  }
  let sibling = path.at(-1) ?? null;
  for (let index = path.length - 2; index >= 0; index -= 1) {
    const parent = path[index + 1];
    if (blocksIndicator(parent)) {
      return rowmateSibling(parent) ?? sibling;
    }
    if (admitsIndicator(parent)) {
      sibling = path[index];
    }
  }
  return sibling;
};

/**
 * The pane a margin indicator portals into: the nearest scrolling ancestor marked
 * as an indicator pane — skipping content-local scrollers (a wide table's overflow
 * wrapper) that would trap it on the text (#18667) — else the nearest scrolling
 * ancestor. Must be a containing block, or the indicator tracks neither content
 * nor scroll.
 */
export function resolveIndicatorScroller(element: HTMLElement | null): HTMLElement | null {
  const nearest = (element && getScrollableAncestor(element)) ?? null;
  for (let scroller = nearest; scroller; scroller = getScrollableAncestor(scroller) ?? null) {
    if (scroller.hasAttribute(ANNOTATION_INDICATOR_PANE_ATTRIBUTE) && isContainingBlock(scroller)) {
      return scroller;
    }
  }
  return nearest && isContainingBlock(nearest) ? nearest : null;
}
